import type React from 'react';
import { X } from 'lucide-react';
import { Button } from 'shared-portal-components-v2';

import type { DebugPanelTab } from '@/hooks/debug/useDebugPanel';

import type { DebugPanelProps } from './DebugPanel.types';
import StorageTab from './tabs/StorageTab';
import BrowserTab from './tabs/BrowserTab';
import LogsTab from './tabs/LogsTab';
import P2PTab from './tabs/P2PTab';
import SessionTab from './tabs/SessionTab';

const TABS: Array<{ id: DebugPanelTab; label: string }> = [
  { id: 'storage', label: 'Storage' },
  { id: 'browser', label: 'Browser' },
  { id: 'logs', label: 'Logs' },
  { id: 'p2p', label: 'P2P' },
  { id: 'session', label: 'Session' },
];

export default function DebugPanel({
  activeTab,
  storageEstimate,
  browserInfo,
  percentFree,
  heightPx,
  logEntries,
  logNamespaces,
  logLevel,
  p2pDiagnostics,
  sessionSnapshot,
  dragHandleProps,
  onClose,
  onTabChange,
  onCopy,
  onCopyAll,
  onSetLogPattern,
  onSetLogLevel,
  onClearLogs,
  onCopyReduxState,
}: DebugPanelProps): React.JSX.Element {
  function renderActiveTab(): React.JSX.Element {
    switch (activeTab) {
      case 'storage':
        return <StorageTab storageEstimate={storageEstimate} percentFree={percentFree} />;
      case 'browser':
        return <BrowserTab browserInfo={browserInfo} />;
      case 'logs':
        return (
          <LogsTab
            logEntries={logEntries}
            logNamespaces={logNamespaces}
            logLevel={logLevel}
            onSetLogPattern={onSetLogPattern}
            onSetLogLevel={onSetLogLevel}
            onClearLogs={onClearLogs}
          />
        );
      case 'p2p':
        return <P2PTab p2pDiagnostics={p2pDiagnostics} />;
      case 'session':
        return <SessionTab sessionSnapshot={sessionSnapshot} onCopyReduxState={onCopyReduxState} />;
    }
  }

  return (
    <aside
      role="complementary"
      aria-label="Diagnostics panel"
      className="fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-border bg-background shadow-lg"
      style={{ height: heightPx }}
    >
      <div
        {...dragHandleProps}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize diagnostics panel"
        className="flex h-2 w-full flex-shrink-0 cursor-row-resize touch-none items-center justify-center border-b border-border bg-muted/40"
      >
        <span className="h-1 w-10 rounded-full bg-border" />
      </div>

      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-2">
        <div className="flex items-center gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                onTabChange(tab.id);
              }}
              className={
                activeTab === tab.id
                  ? 'border-b-2 border-primary px-3 py-1.5 text-sm font-medium text-foreground'
                  : 'px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="primary" size="md" onClick={onCopy}>
            Copy tab
          </Button>
          <Button variant="secondary" size="md" onClick={onCopyAll}>
            Copy all
          </Button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close diagnostics panel"
            className="flex-shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">{renderActiveTab()}</div>
    </aside>
  );
}
